// Voice plumbing: listening (Web Speech recognition) and speaking — Azure
// neural voices via the server when configured, otherwise the device's best
// British voice. Answers are spoken sentence by sentence as they stream in,
// so Arlo HQ starts talking within a second or two of the first words.

import { apiUrl } from './api.ts';
import { hasMic, recordUtterance, type Recording } from './recorder.ts';
import { devicePrefs } from './device.ts';

// --- Listening ---------------------------------------------------------------------

type Recognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  maxAlternatives: number;
  onresult: (e: { results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void;
  onend: () => void;
  onerror: (e: { error: string }) => void;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

const Recognizer: (new () => Recognition) | undefined =
  typeof window !== 'undefined'
    ? (((window as unknown as Record<string, unknown>).SpeechRecognition ?? (window as unknown as Record<string, unknown>).webkitSpeechRecognition) as (new () => Recognition) | undefined)
    : undefined;

// Server transcription (Azure Speech) — for WebViews like the Scalefusion
// browser, which have no speech recognition of their own.
let serverStt = false;
// Some WebViews expose webkitSpeechRecognition but it can't reach a service.
let browserBroken = false;

export function setServerSpeech(available: boolean): void {
  serverStt = available;
}

export function listenMode(): 'browser' | 'server' | 'none' {
  if (Recognizer && !browserBroken) return 'browser';
  if (hasMic && serverStt) return 'server';
  return 'none';
}

export function canListen(): boolean {
  return listenMode() !== 'none';
}

export interface Listening {
  stop: () => void;
  abort: () => void;
}

export interface ListenHandlers {
  onText: (text: string) => void;
  onDone: (text: string) => void;
  onError?: (error: string) => void;
  /** Microphone level 0–1 (server mode only). */
  onLevel?: (level: number) => void;
}

/** Listen for one utterance. `onDone` gets the final text ('' if nothing was heard). */
export function listen(handlers: ListenHandlers): Listening | undefined {
  const mode = listenMode();
  if (mode === 'server') return listenViaServer(handlers);
  if (mode !== 'browser' || !Recognizer) return undefined;
  const r = new Recognizer();
  r.lang = 'en-GB';
  r.interimResults = true;
  r.continuous = false;
  r.maxAlternatives = 1;
  let text = '';
  let aborted = false;
  r.onresult = (e) => {
    let final = '';
    let interim = '';
    for (let i = 0; i < e.results.length; i++) {
      const res = e.results[i];
      if (res.isFinal) final += res[0].transcript;
      else interim += res[0].transcript;
    }
    text = (final + interim).trim();
    handlers.onText(text);
  };
  let switched: Listening | undefined;
  let failed = false;
  r.onerror = (e) => {
    // Recognition that can't reach its service (common in WebViews): switch to the server for good.
    if ((e.error === 'network' || e.error === 'service-not-allowed' || e.error === 'language-not-supported') && hasMic && serverStt) {
      browserBroken = true;
      aborted = true;
      switched = listenViaServer(handlers);
      return;
    }
    if (e.error !== 'no-speech' && e.error !== 'aborted') {
      // Report the error only — a following onDone('') would overwrite the message.
      failed = true;
      handlers.onError?.(e.error);
    }
  };
  r.onend = () => {
    if (!aborted && !failed) handlers.onDone(text);
  };
  try {
    r.start();
  } catch {
    return undefined;
  }
  return {
    stop: () => (switched ? switched.stop() : r.stop()),
    abort: () => {
      aborted = true;
      if (switched) switched.abort();
      else r.abort();
    },
  };
}

function listenViaServer(h: ListenHandlers): Listening {
  let rec: Recording | undefined;
  let cancelled = false;
  let stopEarly = false;
  void (async () => {
    try {
      rec = await recordUtterance({ onLevel: h.onLevel, onSpeech: () => h.onText('…') });
      if (cancelled) return rec.abort();
      if (stopEarly) rec.stop();
      const wav = await rec.done;
      if (cancelled) return;
      if (!wav) return h.onDone('');
      h.onText('…');
      const d = devicePrefs();
      const res = await fetch(apiUrl('/api/stt'), { method: 'POST', headers: { 'Content-Type': 'audio/wav', ...(d.key ? { Authorization: `Bearer ${d.key}` } : {}) }, body: wav });
      const j = (await res.json().catch(() => ({}))) as { text?: string; error?: string };
      if (cancelled) return;
      if (!res.ok) {
        h.onError?.('server');
        return h.onDone('');
      }
      if (j.text) h.onText(j.text);
      h.onDone(j.text ?? '');
    } catch (err) {
      if (cancelled) return;
      const name = (err as Error).name;
      h.onError?.(name === 'NotAllowedError' || name === 'SecurityError' ? 'not-allowed' : 'audio-capture');
      h.onDone('');
    }
  })();
  return {
    stop: () => (rec ? rec.stop() : (stopEarly = true)),
    abort: () => {
      cancelled = true;
      rec?.abort();
    },
  };
}

// --- Text helpers ----------------------------------------------------------------------

/** Turn a (possibly markdown) answer into something pleasant to hear. */
export function stripForSpeech(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/https?:\/\/\S+/g, '')
    .replace(/^\s*#{1,6}\s+/gm, '')
    .replace(/^\s*[-*•]\s+/gm, '')
    .replace(/^\s*\d+\.\s+/gm, '')
    .replace(/[*_`>#]/g, '')
    .replace(/\s*→\s*/g, ', then ')
    .replace(/\s+–\s+|\s+—\s+/g, ', ')
    .replace(/(\d)\s*[–-]\s*(\d)/g, '$1 to $2')
    .replace(/\be\.g\.\s*/gi, 'for example ')
    .replace(/\bi\.e\.\s*/gi, 'that is ')
    .replace(/\bapprox\.\s*/gi, 'about ')
    .replace(/(\d)\s?g\b/g, '$1 grams')
    .replace(/(\d)\s?kg\b/g, '$1 kilos')
    .replace(/(\d)\s?mins?\b/g, '$1 minutes')
    .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/** Splits streamed text into speakable sentences as soon as each one ends. */
export class SentenceSplitter {
  private buf = '';

  feed(delta: string): string[] {
    this.buf += delta;
    const out: string[] = [];
    // A sentence ends at . ! ? followed by whitespace (so "4.5" never splits), or at a line break.
    const re = /[.!?…]+["”’)]?\s+|\n+/g;
    let start = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(this.buf))) {
      const piece = this.buf.slice(start, m.index + m[0].length).trim();
      if (/\b(e\.g|i\.e|approx|vs|etc|Dr|Mr|Mrs|Ms|St)\.$/i.test(piece)) continue;
      if (piece) out.push(piece);
      start = re.lastIndex;
    }
    this.buf = this.buf.slice(start);
    return mergeShort(out);
  }

  flush(): string[] {
    const s = this.buf.trim();
    this.buf = '';
    return s ? [s] : [];
  }
}

function mergeShort(list: string[]): string[] {
  // Very short fragments ("Yes.") sound choppy on their own — join them to the next.
  const out: string[] = [];
  let carry = '';
  for (const s of list) {
    const joined = carry ? `${carry} ${s}` : s;
    if (joined.length < 18) carry = joined;
    else {
      out.push(joined);
      carry = '';
    }
  }
  if (carry) out.push(carry);
  return out;
}

// "Did a wee", "was sick", "has had his dinner" are things to log, so did/was/has
// only count with a "?"; "is" only in forms like "is he…", "is it…".
const QUESTION_START = /^(how|what|what's|whats|why|when|where|which|who|should|shall|can|could|are|does|do|will|would|tell me|explain|help|any tips|give me|remind me|is (?:it|he|there|that|this|my|our|a|an|the)|ok so|so how)\b/i;

/** Is this a question for Ask (rather than something to log)? */
export function looksLikeQuestion(text: string): boolean {
  const t = text.trim().replace(/^(hey |ok |okay |so |um |erm |arlo hq,? |arlo,? )+/i, '');
  return /\?\s*$/.test(t) || QUESTION_START.test(t);
}

// --- Speaking ----------------------------------------------------------------------------

let audioEl: HTMLAudioElement | undefined;
/** A valid 0.05 s silent WAV, built in code (used to unlock audio on mobile). */
function silentWav(): string {
  const samples = 400;
  const buf = new ArrayBuffer(44 + samples * 2);
  const v = new DataView(buf);
  const str = (o: number, t: string) => [...t].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  str(0, 'RIFF');
  v.setUint32(4, 36 + samples * 2, true);
  str(8, 'WAVEfmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, 8000, true);
  v.setUint32(28, 16000, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, 'data');
  v.setUint32(40, samples * 2, true);
  let bin = '';
  new Uint8Array(buf).forEach((b) => (bin += String.fromCharCode(b)));
  return `data:audio/wav;base64,${btoa(bin)}`;
}

/**
 * Call from inside a tap handler: mobile browsers only allow sound that
 * starts from a user gesture, so we "unlock" one audio element and the
 * speech engine now and reuse them for the answer.
 */
export function unlockAudio(): void {
  try {
    audioEl ??= new Audio();
    audioEl.src = silentWav();
    void audioEl.play().catch(() => undefined);
  } catch {
    /* ignore */
  }
  try {
    if ('speechSynthesis' in window) {
      const u = new SpeechSynthesisUtterance(' ');
      u.volume = 0;
      speechSynthesis.speak(u);
    }
  } catch {
    /* ignore */
  }
}

let voicesCache: SpeechSynthesisVoice[] = [];
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  voicesCache = speechSynthesis.getVoices();
  speechSynthesis.addEventListener?.('voiceschanged', () => (voicesCache = speechSynthesis.getVoices()));
}

/** Can this browser speak by itself? (Android WebViews can't.) */
export function canSpeakOnDevice(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/** The nicest British voice on this device. */
export function bestDeviceVoice(): SpeechSynthesisVoice | undefined {
  const gb = voicesCache.filter((v) => /en[-_]GB/i.test(v.lang));
  const score = (v: SpeechSynthesisVoice) =>
    (/natural|neural|premium|enhanced/i.test(v.name) ? 40 : 0) + (/google/i.test(v.name) ? 20 : 0) + (/female|sonia|libby|serena|kate|martha|stephanie/i.test(v.name) ? 5 : 0) + (v.localService ? 0 : 3);
  return gb.sort((a, b) => score(b) - score(a))[0] ?? voicesCache.find((v) => /^en/i.test(v.lang));
}

export interface SpeakerOptions {
  neural: boolean;
  onStart?: () => void;
  onIdle?: () => void;
  /** Quieter at night on the kiosk. */
  volume?: number;
}

/** Speaks queued sentences in order, fetching neural audio ahead of time. */
export class Speaker {
  private queue: { text: string; audio?: Promise<string | undefined> }[] = [];
  private playing = false;
  private stopped = false;
  /** Resolves the sentence currently playing (so stop() never leaves it hanging). */
  private finishCurrent?: () => void;
  private opts: SpeakerOptions;
  speaking = false;

  constructor(opts: SpeakerOptions) {
    this.opts = opts;
  }

  say(sentence: string): void {
    const text = stripForSpeech(sentence);
    if (!text || this.stopped) return;
    this.queue.push({ text, audio: this.opts.neural ? this.fetchAudio(text) : undefined });
    if (!this.playing) void this.next();
  }

  private async fetchAudio(text: string): Promise<string | undefined> {
    const d = devicePrefs();
    try {
      const res = await fetch(apiUrl('/api/tts'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(d.key ? { Authorization: `Bearer ${d.key}` } : {}) },
        body: JSON.stringify({ text, voice: d.voice, rate: d.voiceRate }),
      });
      if (!res.ok) return undefined;
      return URL.createObjectURL(await res.blob());
    } catch {
      return undefined;
    }
  }

  private async next(): Promise<void> {
    const item = this.queue.shift();
    if (!item || this.stopped) {
      this.playing = false;
      if (this.speaking) {
        this.speaking = false;
        this.opts.onIdle?.();
      }
      return;
    }
    this.playing = true;
    if (!this.speaking) {
      this.speaking = true;
      this.opts.onStart?.();
    }
    const url = item.audio ? await item.audio : undefined;
    if (this.stopped) {
      if (url) URL.revokeObjectURL(url);
      return;
    }
    if (url) await this.playUrl(url);
    else await this.speakDevice(item.text);
    void this.next();
  }

  private playUrl(url: string): Promise<void> {
    return new Promise((resolve) => {
      audioEl ??= new Audio();
      const a = audioEl;
      const done = () => {
        a.onended = a.onerror = null;
        this.finishCurrent = undefined;
        URL.revokeObjectURL(url);
        resolve();
      };
      this.finishCurrent = done;
      a.onended = done;
      a.onerror = done;
      a.volume = this.opts.volume ?? 1;
      a.src = url;
      a.play().catch(() => done());
    });
  }

  private speakDevice(text: string): Promise<void> {
    return new Promise((resolve) => {
      if (!('speechSynthesis' in window)) return resolve();
      const u = new SpeechSynthesisUtterance(text);
      const v = bestDeviceVoice();
      if (v) u.voice = v;
      u.lang = v?.lang ?? 'en-GB';
      u.rate = 1 + (devicePrefs().voiceRate ?? 0) / 100;
      u.volume = this.opts.volume ?? 1;
      this.finishCurrent = () => resolve();
      u.onend = () => resolve();
      u.onerror = () => resolve();
      speechSynthesis.speak(u);
      // Some engines never fire onend; don't hang forever.
      setTimeout(resolve, Math.max(4000, text.length * 110));
    });
  }

  /**
   * Stop talking. Deliberately does not call onIdle: stopping isn't "finished",
   * and callers use onIdle to start listening again.
   */
  stop(): void {
    this.stopped = true;
    for (const q of this.queue) void q.audio?.then((u) => u && URL.revokeObjectURL(u));
    this.queue = [];
    try {
      audioEl?.pause();
    } catch {
      /* ignore */
    }
    try {
      speechSynthesis.cancel();
    } catch {
      /* ignore */
    }
    this.finishCurrent?.();
    this.playing = false;
    this.speaking = false;
  }
}
